import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ImpersonatedUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

interface ImpersonationState {
  // The user being impersonated (null = not impersonating)
  impersonatedUser: ImpersonatedUser | null;
  
  // Whether impersonation is active
  isImpersonating: boolean;
  
  // Actions
  startImpersonation: (user: ImpersonatedUser) => void;
  stopImpersonation: () => void;
  
  // Helper to get the effective user ID (impersonated or real)
  getEffectiveUserId: (realUserId: string | null) => string | null;
}

export const useImpersonationStore = create<ImpersonationState>()(
  persist(
    (set, get) => ({
      impersonatedUser: null,
      isImpersonating: false,

      startImpersonation: (user) => {
        console.log('👤 [Impersonation] Starting impersonation as:', user.email);
        set({
          impersonatedUser: user,
          isImpersonating: true,
        });
      },

      stopImpersonation: () => {
        console.log('👤 [Impersonation] Stopping impersonation');
        set({
          impersonatedUser: null,
          isImpersonating: false,
        });
      },

      getEffectiveUserId: (realUserId) => {
        const state = get();
        if (state.isImpersonating && state.impersonatedUser) {
          return state.impersonatedUser.id;
        }
        return realUserId;
      },
    }),
    {
      name: 'impersonation-storage',
      partialize: (state) => ({
        impersonatedUser: state.impersonatedUser,
        isImpersonating: state.isImpersonating,
      }),
    }
  )
);

// Helper hook to get display name for impersonated user
export const getImpersonatedDisplayName = (user: ImpersonatedUser | null): string => {
  if (!user) return '';
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return fullName || user.email.split('@')[0];
};
